import Typography from '@mui/material/Typography';
import { IconName } from '@packages/shared-ui';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@packages/shared-ui/v4/button';
import { useState } from 'react';
import type { SubscriptionOfferCF } from '@/types';
import AgreementAccordion from './AgreementAccordion';
import useStyles from './SignAgreementsView.styles';
import { getOfferMembership, getOfferProducts } from './subscriptionOffer';

interface Props {
  offer: SubscriptionOfferCF;
  onBack: () => void;
  onContinue: () => void;
}

export default function SignAgreementsView({
  offer,
  onBack,
  onContinue,
}: Props) {
  const { classes } = useStyles();

  const agreements = [
    getOfferMembership(offer),
    ...getOfferProducts(offer),
  ].flatMap((product) =>
    product?.fields.termsAndConditions
      ? [
          {
            id: product.sys.id,
            title: product.fields.name,
            terms: product.fields.termsAndConditions,
          },
        ]
      : []
  );

  const [expandedId, setExpandedId] = useState<string | null>(
    agreements[0]?.id ?? null
  );
  const [agreedIds, setAgreedIds] = useState<ReadonlySet<string>>(new Set());
  const hasAgreedToAll = agreements.every(({ id }) => agreedIds.has(id));

  const toggleExpanded = (id: string) =>
    setExpandedId(expandedId === id ? null : id);

  const setAgreed = (id: string, isAgreed: boolean) => {
    const next = new Set(agreedIds);
    if (isAgreed) {
      next.add(id);
    } else {
      next.delete(id);
    }
    setAgreedIds(next);
  };

  return (
    <>
      <div className={classes.header}>
        <Button
          variant={ButtonVariant.GHOST}
          iconName={IconName.ARROW_LEFT}
          aria-label='Back'
          className={classes.backButton}
          onClick={onBack}
        />
        <Typography variant='h2' component='h1' className={classes.heading}>
          Please agree to the terms and conditions
        </Typography>
      </div>
      <section aria-label='Terms and conditions' className={classes.agreements}>
        {agreements.length === 0 && (
          <Typography variant='bodyMd' color='semantic.text.secondary'>
            No terms and conditions are available for this selection yet.
          </Typography>
        )}
        {agreements.map(({ id, title, terms }) => (
          <AgreementAccordion
            key={id}
            title={title}
            terms={terms}
            isExpanded={expandedId === id}
            isAgreed={agreedIds.has(id)}
            onToggle={() => toggleExpanded(id)}
            onAgreedChange={(isAgreed) => setAgreed(id, isAgreed)}
          />
        ))}
      </section>
      <Button
        size={{ xs: ButtonSize.SM, md: ButtonSize.MD }}
        fullWidth
        disabled={agreements.length === 0 || !hasAgreedToAll}
        className={classes.continueButton}
        onClick={onContinue}
      >
        Continue
      </Button>
    </>
  );
}
